import React, { useMemo, useState } from 'react';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

export const INK    = '#2B2620';
export const WALNUT = '#8B4513';
export const MOSS   = '#3B6B44';
export const OCHRE  = '#A6752A';
export const STONE  = '#8C8275';
export const PAPER  = '#FDFBF8';
export const FONT_DISPLAY = "'Fraunces', Georgia, serif";
export const FONT_SANS    = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

export const PAGE_SIZE = 20;

export const cardStyle = { background: PAPER, borderRadius: '16px', padding: '20px 24px', border: '1px solid rgba(139,69,19,0.1)', boxShadow: '0 2px 12px rgba(139,69,19,0.06)', marginBottom: '24px' };
export const selectStyle = {
  padding: '9px 12px', border: '1px solid rgba(139,69,19,0.2)', borderRadius: '8px',
  fontSize: '13px', fontFamily: FONT_SANS, outline: 'none', background: PAPER, color: INK,
};

export const pct = r => `${r.pctOfTotal.toFixed(1)}%`;

export function StatCard({ label, value, sub, color }) {
  return (
    <div style={{ flex: '1 1 160px', background: 'white', border: `1px solid ${color}30`, borderRadius: '12px', padding: '16px 18px' }}>
      <div style={{ fontSize: '11px', fontWeight: '600', color: STONE, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>{label}</div>
      <div style={{ fontFamily: FONT_DISPLAY, fontSize: '22px', fontWeight: '600', color: INK, lineHeight: 1.15 }}>{value}</div>
      {sub && <div style={{ fontSize: '12px', color, marginTop: '4px', fontWeight: 500 }}>{sub}</div>}
    </div>
  );
}

function SortableTh({ label, field, sortField, sortDir, onSort, align }) {
  const active = sortField === field;
  return (
    <th
      onClick={() => onSort(field)}
      style={{
        padding: '10px 14px', textAlign: align || 'left', fontSize: '11px', fontWeight: '700',
        color: active ? WALNUT : STONE, textTransform: 'uppercase', letterSpacing: '0.03em',
        cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }}>
        {label}
        {active && (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
      </span>
    </th>
  );
}

export function sortRows(rows, field, dir) {
  const mult = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = a[field];
    const bv = b[field];
    if (typeof av === 'string') return av.localeCompare(bv) * mult;
    return ((av ?? 0) - (bv ?? 0)) * mult;
  });
}

export function downloadCsv(headers, rows, filename) {
  const csv = [headers.join(','), ...rows.map(r => r.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Self-contained sortable + paginated table. Keyed by the caller with the
// active tab's name so switching tabs remounts it (fresh sort/page state)
// instead of carrying over a sort field the new tab doesn't have.
export function DataTable({ columns, rows, defaultSort, minWidth, emptyLabel }) {
  const [sortField, setSortField] = useState(defaultSort);
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);

  const sorted = useMemo(() => sortRows(rows, sortField, sortDir), [rows, sortField, sortDir]);
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const clampedPage = Math.min(page, totalPages);
  const pageRows = sorted.slice((clampedPage - 1) * PAGE_SIZE, clampedPage * PAGE_SIZE);

  function handleSort(field) {
    if (field === sortField) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('desc'); }
    setPage(1);
  }

  if (rows.length === 0) {
    return <div style={{ padding: '40px 20px', textAlign: 'center', color: STONE, fontSize: '13px' }}>{emptyLabel || 'No data for this range.'}</div>;
  }

  return (
    <>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: minWidth || '520px' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid rgba(139,69,19,0.12)' }}>
              {columns.map(c => (
                <SortableTh key={c.field} label={c.label} field={c.field} sortField={sortField} sortDir={sortDir} onSort={handleSort} align={c.align} />
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r, i) => (
              <tr key={r.key} style={{ borderBottom: '1px solid rgba(139,69,19,0.06)', background: i % 2 ? 'rgba(139,69,19,0.02)' : 'transparent' }}>
                {columns.map(c => (
                  <td key={c.field} style={{ padding: '10px 14px', fontSize: '13px', color: c.emphasize ? WALNUT : (c.muted ? STONE : INK), fontWeight: c.emphasize ? 700 : (c.bold ? 600 : 400), textAlign: c.align || 'left' }}>
                    {c.render ? c.render(r) : r[c.field]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderTop: '1px solid rgba(139,69,19,0.08)' }}>
          <span style={{ fontSize: '12px', color: STONE }}>
            Showing {(clampedPage - 1) * PAGE_SIZE + 1}–{Math.min(clampedPage * PAGE_SIZE, sorted.length)} of {sorted.length}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={clampedPage === 1}
              style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 10px', borderRadius: '6px', border: '1px solid rgba(139,69,19,0.2)', background: 'white', color: clampedPage === 1 ? STONE : INK, cursor: clampedPage === 1 ? 'not-allowed' : 'pointer', fontSize: '12px', opacity: clampedPage === 1 ? 0.5 : 1 }}
            >
              <ChevronLeft size={13} /> Prev
            </button>
            <span style={{ fontSize: '12px', color: INK, fontWeight: 600 }}>Page {clampedPage} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={clampedPage === totalPages}
              style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 10px', borderRadius: '6px', border: '1px solid rgba(139,69,19,0.2)', background: 'white', color: clampedPage === totalPages ? STONE : INK, cursor: clampedPage === totalPages ? 'not-allowed' : 'pointer', fontSize: '12px', opacity: clampedPage === totalPages ? 0.5 : 1 }}
            >
              Next <ChevronRight size={13} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export function MonthTrend({ byMonth }) {
  const maxMonthQty = Math.max(1, ...byMonth.map(m => m.totalQty));
  return (
    <div style={{ padding: '20px' }}>
      {byMonth.length === 0 ? (
        <p style={{ color: STONE, fontSize: '13px', margin: 0 }}>No dated orders in this range.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {byMonth.map(m => (
            <div key={m.key} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '90px', fontSize: '12px', color: STONE, flexShrink: 0 }}>{m.label}</div>
              <div style={{ flex: 1, background: 'rgba(139,69,19,0.06)', borderRadius: '6px', height: '20px', position: 'relative' }}>
                <div style={{ width: `${(m.totalQty / maxMonthQty) * 100}%`, background: WALNUT, height: '100%', borderRadius: '6px', minWidth: '4px' }} />
              </div>
              <div style={{ width: '70px', fontSize: '13px', color: INK, fontWeight: 600, textAlign: 'right', flexShrink: 0 }}>{m.totalQty} units</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
